import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { Table } from "@package/subframe/components/Table";
import { FeatherAlertTriangle } from "@subframe/core";
import { format } from "date-fns";
import type { ReactNode } from "react";

import { WorkspaceStatusBadge } from "@/components/checklists/WorkspaceStatusBadge";
import { TintedEntityIcon } from "@/components/ui/entity-icon";
import { SortableHeaderCell } from "@/components/ui/sortable-header-cell";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";
import type { CategoryResponseDto } from "@/generated/model/categoryResponseDto";
import type { FollowUpActionResponseDto } from "@/generated/model/followUpActionResponseDto";
import type { SortDir } from "@/lib/category-display";
import { categoryColor } from "@/lib/category-display";
import { sourceTypeLabel } from "@/lib/follow-up-actions/follow-up-actions-list";
import {
  assigneeLabel,
  completedByLabel,
  taskDueStatusPill,
  type TaskManagerSortColumn,
} from "@/lib/task-manager";

interface TaskManagerTableProps {
  items: FollowUpActionResponseDto[];
  categories: CategoryResponseDto[];
  /** Every row belongs to the selected nursery, which is the Location column. */
  nurseryName: string;
  /** History shows the completed date/time in place of the due date. */
  isHistory: boolean;
  activeColumn?: TaskManagerSortColumn;
  activeDir: SortDir;
  onSort: (column: TaskManagerSortColumn) => void;
  onView: (id: string) => void;
  /** Optional per-row overflow menu (context actions), rendered beside View. */
  renderRowMenu?: (action: FollowUpActionResponseDto) => ReactNode;
}

export function TaskManagerTable({
  items,
  categories,
  nurseryName,
  isHistory,
  activeColumn,
  activeDir,
  onSort,
  onView,
  renderRowMenu,
}: TaskManagerTableProps) {
  const categoryById = new Map(categories.map((c) => [c.id, c]));
  // Only History has completions to attribute. Header and body both gate on
  // this one flag: a plain semantic table has no column config, so an uneven
  // edit would silently shift every cell right of the insertion point.
  const showCompletedBy = isHistory;

  return (
    <Table
      className={showCompletedBy ? TABLE_MIN_WIDTH.xl : TABLE_MIN_WIDTH.lg}
      header={
        <Table.HeaderRow>
          <SortableHeaderCell
            column="action"
            label="Task"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="category"
            label="Category"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="type"
            label="Type"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          {/* Location is constant across the page, so it is not sortable — the
              list is always scoped to one nursery. */}
          <Table.HeaderCell>Location</Table.HeaderCell>
          {/* History renders completedAt in this cell, so it must sort by it. */}
          <SortableHeaderCell
            column={isHistory ? "completedAt" : "dueDate"}
            label={isHistory ? "Completed" : "Due date"}
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="assignee"
            label="Assigned to"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          {/* Unsortable: there is no completer column or relation on the table
              for the API to order by. */}
          {showCompletedBy && <Table.HeaderCell>Completed by</Table.HeaderCell>}
          <SortableHeaderCell
            column="dueStatus"
            label="Status"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <Table.HeaderCell variant="aligned-right">Actions</Table.HeaderCell>
        </Table.HeaderRow>
      }
    >
      {items.map((action) => {
        const category = action.primaryCategoryId
          ? categoryById.get(action.primaryCategoryId)
          : undefined;
        const categoryTint = categoryColor(
          category?.slug,
          category?.color ?? undefined,
        );
        const pill = taskDueStatusPill(action.dueStatus);

        return (
          <Table.Row
            key={action.id}
            clickable
            onClick={() => onView(action.id)}
          >
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <div className="flex items-center gap-3">
                <TintedEntityIcon
                  entity="follow-up-action"
                  slug={category?.slug}
                  color={category?.color ?? undefined}
                  fallbackIcon={<FeatherAlertTriangle />}
                />
                <span className="text-body-bold font-body-bold text-neutral-900">
                  {action.description}
                </span>
              </div>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              {category ? (
                <span
                  className="text-body-bold font-body-bold"
                  style={categoryTint ? { color: categoryTint } : undefined}
                >
                  {category.name}
                </span>
              ) : (
                <span className="text-caption font-caption text-subtext-color">
                  —
                </span>
              )}
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <Badge variant="neutral">
                {sourceTypeLabel(action.sourceType)}
              </Badge>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <span className="text-caption font-caption text-neutral-900">
                {nurseryName || "—"}
              </span>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <span className="text-caption font-caption text-neutral-900">
                {isHistory
                  ? action.completedAt
                    ? format(new Date(action.completedAt), "d MMM yyyy, h:mm a")
                    : "—"
                  : action.dueDate
                    ? format(new Date(action.dueDate), "d MMM yyyy")
                    : "No due date"}
              </span>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <span className="text-caption font-caption text-neutral-900">
                {assigneeLabel(action)}
              </span>
            </Table.Cell>
            {showCompletedBy && (
              <Table.Cell className="h-14 grow shrink-0 basis-0">
                <span className="text-caption font-caption text-neutral-900">
                  {completedByLabel(action)}
                </span>
              </Table.Cell>
            )}
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              {pill ? (
                <WorkspaceStatusBadge pill={pill} />
              ) : (
                <span className="text-caption font-caption text-subtext-color">
                  —
                </span>
              )}
            </Table.Cell>
            <Table.Cell
              className="h-14 grow shrink-0 basis-0"
              variant="aligned-right"
            >
              <div
                className="flex items-center justify-end gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  variant="neutral-primary"
                  onClick={() => onView(action.id)}
                >
                  View
                </Button>
                {renderRowMenu?.(action)}
              </div>
            </Table.Cell>
          </Table.Row>
        );
      })}
    </Table>
  );
}
